import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-modal";
import { FaEdit } from "react-icons/fa";
import { MaterialReactTable } from 'material-react-table';
import { Box, Typography, Button, IconButton, Tooltip } from '@mui/material'; // Material UI components
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { mkConfig, generateCsv, download } from 'export-to-csv'; // For CSV export
import "./CycloneTrack.css"; // Import the CSS file

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

Modal.setAppElement("#root"); // Ensure this matches your root HTML element ID

const CycloneTrack = () => {
  const [data, setData] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [loading, setLoading] = useState(true); // Set to true initially for loading state

  // Fetch data from Django API
  useEffect(() => {
    setLoading(true);
    fetch(`${backendUrl}cyclone/cyclone-track/`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((apiData) => {
        // Ensure that observed_data and forecast_data are strings for display initially,
        // or handled by custom Cell renderers if you want them pretty-printed.
        // For editing, you'll work with the actual object.
        setData(apiData);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setLoading(false);
        // Optionally set a state to display an error message to the user
      });
  }, []);

  const handleEditClick = (row) => {
    // Create a deep copy of the row, especially for nested objects like observed_data/forecast_data
    // This prevents direct mutation of state and allows canceling edits.
    setEditingRow(JSON.parse(JSON.stringify(row)));
    setModalIsOpen(true);
  };

  const handleChange = (field, value) => {
    setEditingRow((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!editingRow || !editingRow.id) return;

    setLoading(true);
    fetch(`${backendUrl}cyclone/cyclone-track/${editingRow.id}/`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        // Ensure observed_data and forecast_data are objects when sending to API
        body: JSON.stringify({
          ...editingRow,
          // If you have specific formatting requirements for sending to backend, handle here
          // e.g., if backend expects stringified JSON for these fields
          // observed_data: JSON.stringify(editingRow.observed_data),
          // forecast_data: JSON.stringify(editingRow.forecast_data),
        }),
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(() => {
        setData((prevData) =>
          prevData.map((row) => (row.id === editingRow.id ? editingRow : row))
        );
        setModalIsOpen(false);
        setLoading(false);
        alert('Record updated successfully!');
      })
      .catch((error) => {
        console.error("Error updating data:", error);
        setLoading(false);
        alert(`Failed to update record: ${error.message}`);
      });
  };

  // MaterialReactTable Columns definition using useMemo
  const columns = useMemo(
    () => [
      {
        accessorKey: 'edit', // Unique accessorKey for the edit button column
        header: 'Edit',
        size: 50,
        enableColumnFilter: false, // Do not allow filtering on this column
        enableColumnActions: false, // Do not allow actions like hiding on this column
        enableSorting: false, // Do not allow sorting on this column
        Cell: ({ row }) => (
          <Tooltip title="Edit Record">
            <IconButton onClick={() => handleEditClick(row.original)}>
              <FaEdit />
            </IconButton>
          </Tooltip>
        ),
      },
      { accessorKey: 'cyclone_name', header: 'Cyclone Name', size: 150 },
      { accessorKey: 'date', header: 'Date', size: 100 },
      { accessorKey: 'bulletin', header: 'Bulletin', size: 200 },
      // If 'title' or 'observation' are used, uncomment and adjust:
      // { accessorKey: 'title', header: 'Title', size: 150 },
      // { accessorKey: 'observation', header: 'Observation', size: 200 },
      {
        accessorKey: 'observed_data',
        header: 'Observed Data',
        size: 250,
        enableColumnFilter: false, // Filtering complex JSON usually not practical
        Cell: ({ cell }) => (
          <Tooltip title={JSON.stringify(cell.getValue(), null, 2)}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: '100px', overflowY: 'auto' }}>
              {JSON.stringify(cell.getValue())}
            </Typography>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'forecast_data',
        header: 'Forecast Data',
        size: 250,
        enableColumnFilter: false, // Filtering complex JSON usually not practical
        Cell: ({ cell }) => (
          <Tooltip title={JSON.stringify(cell.getValue(), null, 2)}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: '100px', overflowY: 'auto' }}>
              {JSON.stringify(cell.getValue())}
            </Typography>
          </Tooltip>
        ),
      },
      // If 'cyclone_speed' is used:
      // { accessorKey: 'cyclone_speed', header: 'Cyclone Speed (km/h)', size: 100,
      //   Cell: ({ cell }) => `${cell.getValue()} km/h`,
      // },
      { accessorKey: 'max_winds', header: 'Maximum Winds (km/h)', size: 120,
        Cell: ({ cell }) => `${cell.getValue()} km/h`,
      },
    ],
    [],
  );

  // CSV export configuration
  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    // You might want to provide custom headers for more readable CSV
    // columnHeaders: columns.filter(col => col.accessorKey !== 'edit').map(col => ({ key: col.accessorKey, header: col.header })),
  });

  const handleExportData = () => {
    const csvData = data.map(row => {
      // Flatten or format nested JSON for better CSV readability if needed
      const observed = JSON.stringify(row.observed_data);
      const forecast = JSON.stringify(row.forecast_data);
      return {
        ...row,
        observed_data: observed,
        forecast_data: forecast,
      };
    });
    const csv = generateCsv(csvConfig)(csvData);
    download(csvConfig)(csv);
  };

  return (
    <div className="table-container">
      {loading ? (
        <div className="text-center">Loading cyclone track data...</div>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={data}
          enableColumnFilterModes
          enableColumnOrdering
          enableColumnPinning
          enableFacetedValues
          enableGrouping
          enablePagination
          enableRowSelection
          enableSorting
          // Z-index fix for modal overlay
          muiTablePaperProps={{
            sx: {
              zIndex: 1, // Ensure table content is behind modal
            },
          }}
          // Custom header with title and export button
          renderTopToolbarCustomActions={({ table }) => (
            <Box sx={{ display: 'flex', gap: '1rem', p: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mr: 2 }}>
                Cyclone Track Data
              </Typography>
              <Button
                color="primary"
                onClick={handleExportData}
                startIcon={<FileDownloadIcon />}
                variant="contained"
              >
                Export All Data
              </Button>
              {/* Optional: Button to export currently visible/filtered data */}
              {/* <Button
                color="primary"
                onClick={() => handleExportRows(table.getFilteredRowModel().rows)}
                startIcon={<FileDownloadIcon />}
                variant="outlined"
              >
                Export Filtered Data
              </Button> */}
            </Box>
          )}
          muiToolbarAlertBannerProps={
            data.length > 0
              ? { color: 'primary', children: 'Cyclone track data loaded successfully.' }
              : { color: 'warning', children: 'No cyclone track data to display.' }
          }
        />
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        contentLabel="Edit Cyclone Track Record"
        // Z-index fix for modal overlay (must be higher than table's z-index)
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 1000, // Higher than any table z-index
          },
          content: {
            width: "60%",
            height: "80%",
            margin: "auto",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            overflowY: "auto", // Enable scrolling for modal content
            zIndex: 1001, // Content above overlay
          },
        }}
      >
        <h3 className="mb-4">Edit Cyclone Track Record</h3>
        {editingRow && (
          <form>
            <div className="mb-3">
              <label htmlFor="editCycloneName" className="form-label">Name:</label>
              <input
                type="text"
                id="editCycloneName"
                className="form-control"
                value={editingRow.cyclone_name || ""}
                onChange={(e) => handleChange("cyclone_name", e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label htmlFor="editDate" className="form-label">Date:</label>
              <input
                type="date"
                id="editDate"
                className="form-control"
                value={editingRow.date || ""}
                onChange={(e) => handleChange("date", e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label htmlFor="editBulletin" className="form-label">Bulletin:</label>
              <input
                type="text"
                id="editBulletin"
                className="form-control"
                value={editingRow.bulletin || ""}
                onChange={(e) => handleChange("bulletin", e.target.value)}
              />
            </div>

            {/* If title and observation fields are used, uncomment and use appropriate input/textarea */}
            {/* <div className="mb-3">
              <label htmlFor="editTitle" className="form-label">Title:</label>
              <input
                type="text"
                id="editTitle"
                className="form-control"
                value={editingRow.title || ""}
                onChange={(e) => handleChange("title", e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="editObservation" className="form-label">Observation:</label>
              <textarea
                id="editObservation"
                className="form-control"
                rows="3"
                value={editingRow.observation || ""}
                onChange={(e) => handleChange("observation", e.target.value)}
              />
            </div> */}

            <div className="mb-3">
              <label htmlFor="editObservedData" className="form-label">Observed Data (JSON):</label>
              <textarea
                id="editObservedData"
                className="form-control"
                rows="5"
                value={JSON.stringify(editingRow.observed_data, null, 2)}
                onChange={(e) => {
                  try {
                    handleChange("observed_data", JSON.parse(e.target.value));
                  } catch (err) {
                    console.error("Invalid JSON for observed_data:", err);
                    // Optionally show an error message to the user
                  }
                }}
              />
              <small className="form-text text-muted">Enter valid JSON array of objects.</small>
            </div>

            <div className="mb-3">
              <label htmlFor="editForecastData" className="form-label">Forecast Data (JSON):</label>
              <textarea
                id="editForecastData"
                className="form-control"
                rows="5"
                value={JSON.stringify(editingRow.forecast_data, null, 2)}
                onChange={(e) => {
                  try {
                    handleChange("forecast_data", JSON.parse(e.target.value));
                  } catch (err) {
                    console.error("Invalid JSON for forecast_data:", err);
                    // Optionally show an error message to the user
                  }
                }}
              />
              <small className="form-text text-muted">Enter valid JSON array of objects.</small>
            </div>

            {/* If cyclone_speed is used, uncomment */}
            {/* <div className="mb-3">
              <label htmlFor="editCycloneSpeed" className="form-label">Cyclone Speed (km/h):</label>
              <input
                type="number"
                id="editCycloneSpeed"
                className="form-control"
                value={editingRow.cyclone_speed || ""}
                onChange={(e) => handleChange("cyclone_speed", e.target.value)}
              />
            </div> */}

            <div className="mb-3">
              <label htmlFor="editMaxWinds" className="form-label">Maximum Winds (km/h):</label>
              <input
                type="number"
                id="editMaxWinds"
                className="form-control"
                value={editingRow.max_winds || ""}
                onChange={(e) => handleChange("max_winds", e.target.value)}
              />
            </div>

            <div className="d-flex justify-content-end mt-4">
              <button type="button" onClick={handleSave} className="btn btn-primary me-2">Save Changes</button>
              <button type="button" onClick={() => setModalIsOpen(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default CycloneTrack;